/* SPD PDP option/capacity enhancements — loaded as an external, deferred theme script (not inline)
   so it survives Content-Security-Policy rules and script-optimizer apps that strip/defer inline
   <script> tags. All work is confined to `.atc-info--product-options .option-section` and wrapped
   in an IIFE + try/catch, so it can neither leak globals nor throw a page-level error.

   Does three things:
     1. shrinkUnits()   — wraps the "TB"/"GB" unit of each capacity pill in <span class="cap-unit">.
     2. groupOptions()  — repairs pasted list nesting and marks the Options list as .options-row.
     3. buildDropdowns()— mirrors each pill row into a native <select> (Options + Drive Capacity)
                          for mobile. Adds .dd-swapped to a row only after its dropdown is built;
                          mobile CSS hides a row only when it carries .dd-swapped, so if this script
                          never runs the pill rows stay visible instead of the list disappearing.

   NOTE: This handles the metafield-driven pill lists used by hard-drive products. JBOD products use
   NATIVE Shopify variants ("Storage Capacity" option); their checkout-card dropdown is rendered in
   Liquid and wired to the variant engine in product-template.liquid — not here. */
(function () {
  var UNIT_RE = /^(\d+(?:\.\d+)?)\s*(TB|GB|MB|PB)$/i;

  function shrinkUnits() {
    document.querySelectorAll('.atc-info--product-options .option-section a').forEach(function (a) {
      if (a.querySelector('.cap-unit')) return;            // already processed
      var m = a.textContent.trim().match(UNIT_RE);
      if (!m) return;                                       // not a capacity pill
      a.innerHTML = m[1] + '<span class="cap-unit">' + m[2] + '</span>';
    });
  }

  // True when a pill list is the Drive Capacity list (its items look like "18TB"/"512GB").
  function isCapacityList(anchors) {
    if (!anchors.length) return false;
    var cap = 0;
    anchors.forEach(function (a) { if (UNIT_RE.test(a.textContent.trim())) cap++; });
    return cap >= Math.ceil(anchors.length / 2);
  }
  function directAnchors(el) {
    return Array.prototype.slice.call(el.querySelectorAll(':scope > li a'));
  }

  // An unclosed Options <ul> can absorb the next heading and capacity list. Split it
  // around non-list content, preserving order and the existing links. A list contains
  // only <li>s; headings, subsequent lists and the mobile selects remain its siblings.
  function groupOptions() {
    document.querySelectorAll('.atc-info--product-options .option-section').forEach(function (sec) {
      Array.prototype.slice.call(sec.querySelectorAll('ul')).forEach(function (list) {
        var nodes = Array.prototype.slice.call(list.childNodes);
        var invalid = nodes.some(function (node) {
          return node.nodeType === 1 && !/^(LI|SCRIPT|TEMPLATE)$/.test(node.tagName);
        });
        if (!invalid) return;
        var fragment = document.createDocumentFragment();
        var run = null;
        var first = true;
        nodes.forEach(function (node) {
          if (node.nodeType === 1 && !/^(LI|SCRIPT|TEMPLATE)$/.test(node.tagName)) {
            run = null;
            fragment.appendChild(node);
          } else if (node.nodeType === 1 && node.tagName === 'LI') {
            if (!run) {
              run = list.cloneNode(false);
              if (!first) run.removeAttribute('id');
              first = false;
              fragment.appendChild(run);
            }
            run.appendChild(node);
          } else if (run) {
            run.appendChild(node);
          } else {
            fragment.appendChild(node);
          }
        });
        list.parentNode.replaceChild(fragment, list);
      });
      var ul = sec.querySelector(':scope > ul');
      if (!ul) return;
      if (isCapacityList(directAnchors(ul))) return;                     // first list is capacity → leave it
      ul.classList.add('options-row');
    });
  }

  // Build a native <select> mirror of each pill list (Options + Drive Capacity) for mobile.
  // Structure-agnostic: any container that directly holds <li><a> pills becomes a dropdown, and
  // its label is decided by content (capacity vs options) rather than by nesting position — so it
  // works whether the two lists are nested or separate siblings. Each pill is an <a href> to
  // another product; the current one has no href + .selected-option.
  function buildDropdowns() {
    document.querySelectorAll('.atc-info--product-options .option-section').forEach(function (sec) {
      var containers = Array.prototype.slice.call(sec.querySelectorAll('.options-row, ul')).filter(function (el) {
        return el.querySelector(':scope > li a');                       // holds pills directly
      });
      containers.forEach(function (el) {
        var prev = el.previousElementSibling;
        if (prev && prev.classList && prev.classList.contains('opt-dd')) return;  // already built
        var anchors = directAnchors(el);
        if (!anchors.length) return;
        var sel = document.createElement('select');
        sel.className = 'opt-dd';
        sel.setAttribute('aria-label', isCapacityList(anchors) ? 'Drive Capacity' : 'Options');
        anchors.forEach(function (a) {
          var opt = document.createElement('option');
          var href = a.getAttribute('href');
          var current = a.closest('li').classList.contains('selected-option') || !href;
          opt.value = current ? '' : (href || '');
          opt.textContent = a.textContent.trim().replace(/\s+/g, ' ');
          if (current) opt.selected = true;
          sel.appendChild(opt);
        });
        // Arrow keys fire change on a closed select, so a keyboard user would be sent to
        // another product while still browsing the list. Hold the jump until Enter or blur.
        // Enter, Space and Alt+Arrow open the native picker instead; a choice made in there
        // is deliberate, so it navigates at once like a mouse pick.
        var viaKeyboard = false, pendingHref = '';
        function go() {
          if (pendingHref) { var href = pendingHref; pendingHref = ''; window.location.href = href; }
        }
        sel.addEventListener('keydown', function (e) {
          viaKeyboard = !(e.key === 'Enter' || e.key === ' ' || e.altKey);
        });
        sel.addEventListener('mousedown', function () { viaKeyboard = false; });
        sel.addEventListener('change', function () {
          if (!sel.value) return;
          if (viaKeyboard) { pendingHref = sel.value; return; }
          window.location.href = sel.value;
        });
        sel.addEventListener('keyup', function (e) { if (e.key === 'Enter') go(); });
        sel.addEventListener('blur', go);
        el.parentNode.insertBefore(sel, el);
        el.classList.add('dd-swapped');
      });
    });
  }

  // Hide/show the swapped pill rows in JS rather than relying on CSS alone.
  //
  // Why not CSS: another stylesheet on the page (cross-origin, so not inspectable)
  // forces `display:flex` on these lists with a precedence that beats an author
  // `!important` rule here — verified by injecting one at runtime and watching the
  // element stay `flex`. An inline style set with the "important" priority is the
  // one thing that reliably wins, and JS is also the only place that knows a
  // dropdown was actually built. The CSS rule stays as a belt-and-braces fallback.
  var MOBILE_MQ = '(max-width: 768px)';
  function syncSwappedRows() {
    var mobile = window.matchMedia(MOBILE_MQ).matches;
    document.querySelectorAll('.atc-info--product-options .option-section .dd-swapped')
      .forEach(function (row) {
        if (mobile) {
          row.style.setProperty('display', 'none', 'important');
        } else {
          row.style.removeProperty('display');   // desktop: pills again
        }
      });
  }

  function run() {
    try { shrinkUnits(); } catch (e) { /* leave capacity text as-is */ }
    try { groupOptions(); } catch (e) { /* leave option pills ungrouped */ }
    try { buildDropdowns(); } catch (e) { /* pill rows stay visible (see .dd-swapped) */ }
    try { syncSwappedRows(); } catch (e) { /* rows stay visible — no worse than before */ }
  }

  try {
    var mq = window.matchMedia(MOBILE_MQ);
    var onChange = function () { try { syncSwappedRows(); } catch (e) {} };
    if (mq.addEventListener) { mq.addEventListener('change', onChange); }
    else if (mq.addListener) { mq.addListener(onChange); }   // older Safari
  } catch (e) { /* no live re-sync; initial state still correct */ }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  } catch (e) { /* no-op */ }
})();
